import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useQueueDeviceCommandMutation } from '../../store/api/commandApi';
import type { Device } from '../../types/api';
import { useHasChanged } from '@hooks/useHasChanged';

interface DPSEnvironmentDialogProps {
  open: boolean;
  onClose: () => void;
  device: Device;
}

const DPSEnvironmentDialog: React.FC<DPSEnvironmentDialogProps> = ({ open, onClose, device }) => {
  // Follow the device's environment; DFO is read-only and cannot be set via UI
  const settableEnvironment =
    device.dpsEnvironment === 'Production' || device.dpsEnvironment === 'UAT' ? device.dpsEnvironment : null;
  const [selectedEnvironment, setSelectedEnvironment] = useState(settableEnvironment);
  const [queueCommand, { isLoading, error, isSuccess, reset }] = useQueueDeviceCommandMutation();
  const [isQuerying, setIsQuerying] = useState(false);

  if (useHasChanged(device.dpsEnvironment) && settableEnvironment) {
    setSelectedEnvironment(settableEnvironment);
  }

  // Auto-close on success after 2 seconds
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        reset();
        setIsQuerying(false);
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, reset, onClose]);

  const handleClose = () => {
    reset();
    setIsQuerying(false);
    onClose();
  };

  const handleQueryEnvironment = async () => {
    setIsQuerying(true);
    try {
      await queueCommand({
        deviceId: device.deviceId,
        command: {
          command_type: 'get_dps_environment',
          parameters: {},
        },
      }).unwrap();
    } catch (err) {
      console.error('Failed to query DPS environment:', err);
    } finally {
      setIsQuerying(false);
    }
  };

  const handleSaveEnvironment = async () => {
    if (!selectedEnvironment) return;

    try {
      await queueCommand({
        deviceId: device.deviceId,
        command: {
          command_type: 'set_dps_environment',
          parameters: {
            environment: selectedEnvironment,
          },
        },
      }).unwrap();
    } catch (err) {
      console.error('Failed to set DPS environment:', err);
    }
  };

  const getCurrentEnvironmentInfo = () => {
    const { dpsEnvironment, dpsIdScope } = device;

    if (!dpsEnvironment && !dpsIdScope) {
      return {
        icon: <InfoIcon color="info" />,
        color: 'info' as const,
        text: 'Unknown - Query device to check',
      };
    }

    if (dpsEnvironment === 'Unknown') {
      return {
        icon: <WarningIcon color="warning" />,
        color: 'warning' as const,
        text: `Unknown DpsIdScope: ${dpsIdScope}`,
      };
    }

    return {
      icon: <CheckCircleIcon color="success" />,
      color: 'success' as const,
      text: `${dpsEnvironment} (${dpsIdScope})`,
    };
  };

  const envInfo = getCurrentEnvironmentInfo();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage DPS Environment</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Current Environment Status */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Current Environment
            </Typography>
            <Alert severity={envInfo.color} icon={envInfo.icon}>
              {envInfo.text}
            </Alert>
          </Box>

          {/* Query Current Environment Button */}
          {(!device.dpsEnvironment || device.dpsEnvironment === 'Unknown') && (
            <Box>
              <Button
                variant="outlined"
                onClick={handleQueryEnvironment}
                disabled={isQuerying || isLoading}
                fullWidth
                startIcon={isQuerying ? <CircularProgress size={20} /> : <InfoIcon />}
              >
                {isQuerying ? 'Querying Device...' : 'Query Current Environment'}
              </Button>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mt: 1,
                  display: 'block'
                }}>
                This will read the appsettings.json file from the device to determine the current DPS environment.
              </Typography>
            </Box>
          )}

          {/* Environment Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Change Environment
            </Typography>
            <ToggleButtonGroup
              value={selectedEnvironment}
              exclusive
              onChange={(_, newValue) => setSelectedEnvironment(newValue)}
              fullWidth
              color="primary"
            >
              <ToggleButton value="Production">
                <Stack direction="column" spacing={0.5} sx={{
                  alignItems: 'center'
                }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 'bold'
                  }}>
                    Production
                  </Typography>
                  <Chip label="0ne00CB06EA" size="small" color="success" variant="outlined" />
                </Stack>
              </ToggleButton>
              <ToggleButton value="UAT">
                <Stack direction="column" spacing={0.5} sx={{
                  alignItems: 'center'
                }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 'bold'
                  }}>
                    UAT
                  </Typography>
                  <Chip label="0ne00B24CC7" size="small" color="info" variant="outlined" />
                </Stack>
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                mt: 1,
                display: 'block'
              }}>
              Changing the environment will update the DpsIdScope in appsettings.json and restart the Comnovo Gateway service.
            </Typography>
          </Box>

          {/* Success Message */}
          {isSuccess && (
            <Alert severity="success">
              Command sent successfully! The device will update and restart the service.
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error">
              Failed to send command: {(error as any)?.data?.message || 'Unknown error'}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveEnvironment}
          variant="contained"
          disabled={!selectedEnvironment || selectedEnvironment === device.dpsEnvironment || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Sending...' : 'Apply Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DPSEnvironmentDialog;
